<script lang="ts">
  import Boton from './Boton.svelte';
  import Cielo from '@/lib/escena/Cielo.svelte';
  import Luciernagas from '@/lib/escena/Luciernagas.svelte';
  import Girasoles from '@/lib/escena/Girasoles.svelte';
  import Polen from '@/lib/escena/Polen.svelte';
  import Carta from '@/lib/escena/Carta.svelte';
  import { pasoActual, siguientePaso } from '@/lib/estado/pasos';
  import { bandas, iniciarAudio, tiempoReproduccion } from '@/lib/estado/audio';
  import { FRASES_PLACEHOLDER } from '@/lib/contenido/frases';
  import { ejecutarTransicion } from '@/lib/movimiento/lineaTiempoMaestra';
  import type { PasoId, TonoCielo } from '@/types';

  interface BotonRecorrido {
    id: PasoId;
    etiqueta: string;
  }

  const BOTONES: readonly BotonRecorrido[] = [
    { id: 'SOL', etiqueta: 'Caminar bajo las estrellas' },
    { id: 'MUSICA', etiqueta: 'Despertar las luciérnagas' },
    { id: 'LUCIERNAGAS', etiqueta: 'Hacer florecer el jardín' },
    { id: 'GIRASOLES', etiqueta: 'Llega el amanecer' },
    { id: 'AMANECER', etiqueta: 'Tengo algo que decirte' },
    { id: 'CARTA', etiqueta: 'Para siempre' },
  ] as const;

  function botonSiguiente(actual: PasoId): BotonRecorrido | null {
    return BOTONES.find((b) => b.id === actual) ?? null;
  }

  const TONO_POR_PASO: Record<PasoId, TonoCielo> = {
    INICIO: 'papel',
    SOL: 'crepusculo',
    MUSICA: 'crepusculo',
    LUCIERNAGAS: 'crepusculo',
    GIRASOLES: 'crepusculo',
    AMANECER: 'amanecer',
    CARTA: 'papel',
  };

  const CANTIDAD_LUCIERNAGAS_POR_PASO: Record<PasoId, number> = {
    INICIO: 0,
    SOL: 0,
    MUSICA: 5,
    LUCIERNAGAS: 200,
    GIRASOLES: 200,
    AMANECER: 50,
    CARTA: 0,
  };

  function luciernagasVisibles(paso: PasoId): boolean {
    return CANTIDAD_LUCIERNAGAS_POR_PASO[paso] > 0;
  }

  function girasolesBrotando(paso: PasoId): boolean {
    return paso === 'GIRASOLES' || paso === 'AMANECER';
  }

  function polenVisible(paso: PasoId): boolean {
    return paso === 'AMANECER';
  }

  let forzarAceleracion = $state(false);
  let mostrarPensamientoApurada = $state(false);

  /** Maneja el avance al siguiente paso, acelerando si presiona antes de tiempo en SOL. */
  async function manejarAvance(): Promise<void> {
    const desde = $pasoActual;

    // En el paso SOL (Caminar bajo las estrellas): si el texto/girasol aún no terminaron de salir naturalmente
    const elementosListos = forzarAceleracion || $tiempoReproduccion >= 11;
    if (desde === 'SOL' && !elementosListos) {
      forzarAceleracion = true;
      mostrarPensamientoApurada = true;
      // El primer clic acelera la aparición instantánea y muestra el pensamiento
      return;
    }

    const hastaIdx = ['INICIO', 'SOL', 'MUSICA', 'LUCIERNAGAS', 'GIRASOLES', 'AMANECER', 'CARTA'].indexOf(desde);
    const hasta: PasoId = hastaIdx >= 0 && hastaIdx < 6
      ? (['INICIO', 'SOL', 'MUSICA', 'LUCIERNAGAS', 'GIRASOLES', 'AMANECER', 'CARTA'][hastaIdx + 1] as PasoId)
      : 'CARTA';

    if (desde === 'SOL') {
      void iniciarAudio();
    }

    ejecutarTransicion(desde, hasta, () => {
      siguientePaso();
    });
  }
</script>

<Cielo
  tono={TONO_POR_PASO[$pasoActual]}
  intensidadAgudo={$bandas.agudo}
  intensidadMedio={$bandas.medio}
  intensidadBajo={$bandas.bajo}
/>

<!-- Poema en el paso SOL (atardecer con girasol) -->
{#if $pasoActual === 'SOL' && ($tiempoReproduccion >= 8 || forzarAceleracion)}
  <div class="cartel-poema" aria-live="polite">
    <div class="adorno-poema" aria-hidden="true">✦</div>
    <p class="frase-dedicatoria">
      "Amor mío, mis regalos a veces son curiosos, como el de hoy, donde te dedico un poema animado."
    </p>
    <p class="frase-poema">
      "Hoy las flores empiezan su rebrotar, empiezan a resurgir y mirar hacia el sol, alegres por la llegada de la primavera. Y nuestro amor presencia este cambio por segundo año..."
    </p>
  </div>
{/if}

<!-- Continuación del poema en el paso MUSICA y LUCIERNAGAS (la noche mágica con estrellas y luciérnagas) -->
{#if $pasoActual === 'MUSICA' || $pasoActual === 'LUCIERNAGAS'}
  <div class="cartel-poema" aria-live="polite">
    <div class="adorno-poema" aria-hidden="true">✦ ✦ ✦</div>
    <p class="frase-dedicatoria">
      "Bajo este mismo cielo que ahora se ilumina, cada acorde guarda un recuerdo contigo."
    </p>
    <p class="frase-poema">
      "Las estrellas no compiten con tu luz; solo bailan al ritmo de lo que siento por ti cada día de esta primavera."
    </p>
  </div>
{/if}

<!-- Poema en el paso GIRASOLES (El florecimiento del jardín para Jazmín) -->
{#if $pasoActual === 'GIRASOLES'}
  {@const boton = botonSiguiente($pasoActual)}
  <div class="zona-poema-con-boton">
    <div class="cartel-poema" aria-live="polite">
      <div class="adorno-poema" aria-hidden="true">🌻 ✦ 🌻</div>
      <p class="frase-dedicatoria">
        "Y al fin florecen... porque donde estás tú, todo encuentra motivos para renacer."
      </p>
      <p class="frase-poema">
        "Un jardín entero que lleva tu nombre y tus colores, abriéndose paso en la noche solo para verte sonreír."
      </p>
    </div>
    {#if boton}
      <div class="contenedor-boton-bajo-caja">
        <Boton etiqueta={boton.etiqueta} alPulsar={manejarAvance} variante="recorrido" />
      </div>
    {/if}
  </div>
{/if}

<!-- Pantalla Final: Amanecer con foto encima, caja de texto translúcida en medio y frase al final -->
{#if $pasoActual === 'AMANECER'}
  <div class="escenario-final-amanecer" aria-live="polite">
    <!-- 1. Foto Polaroid independiente encima de la caja -->
    <figure class="polaroid-final">
      <img src="/images/placeholder.jpg" alt="Recuerdo especial" class="img-polaroid-final" />
    </figure>

    <!-- 2. Caja de texto poética translúcida -->
    <div class="caja-texto-amanecer">
      <div class="adorno-amanecer" aria-hidden="true">☀️ ✦ 🌸</div>
      <p class="frase-dedicatoria-amanecer">
        "Con cada amanecer, mi certeza se renueva."
      </p>
      <p class="frase-poema-amanecer">
        "La noche dio paso a la luz de este nuevo día, y entre todas las maravillas que despiertan, la más hermosa siempre serás tú..."
      </p>
    </div>

    <!-- 3. Frase final de cierre independiente debajo de la caja -->
    <div class="frase-cierre-final" aria-label="Dedicatoria de cierre">
      <span class="adorno-cierre">🌱</span>
      <span class="texto-cierre">Sigamos cultivando nuestro amor</span>
      <span class="adorno-cierre">🌻</span>
    </div>
  </div>
{/if}

<!-- Girasol floreciendo suavemente en la base en el paso SOL (natural o acelerado) -->
{#if $pasoActual === 'SOL' && ($tiempoReproduccion >= 11 || forzarAceleracion)}
  <div class="girasol-base-revelado" aria-hidden="true">
    <img
      src="/images/girasol.png"
      alt="Girasoles floreciendo"
      class="girasol-base-img"
    />
    {#if mostrarPensamientoApurada}
      <div class="pensamiento-apurada" aria-live="polite">
        <span class="punto-pensamiento p1"></span>
        <span class="punto-pensamiento p2"></span>
        <div class="burbuja-pensamiento">
          ¿Estás apurada? Dale clic de nuevo al botón para avanzar 😉
        </div>
      </div>
    {/if}
  </div>
{/if}

<!-- Las luciérnagas SOLO se muestran luego de darle al botón 'Despertar las luciérnagas' (paso LUCIERNAGAS) -->
<Luciernagas
  visibles={$pasoActual === 'LUCIERNAGAS'}
  intensidadBajo={$bandas.bajo}
/>

<Girasoles brotando={girasolesBrotando($pasoActual)} sway={$bandas.medio} />

<Polen visibles={polenVisible($pasoActual)} destelloAgudo={$bandas.agudo} />

<!-- Capas de hierba continua del prado para coherencia visual -->
{#if $pasoActual !== 'CARTA'}
  <div class="prado-recorrido" aria-hidden="true">
    <svg class="capa-hierba-recorrido" viewBox="0 0 1200 240" preserveAspectRatio="none">
      <path
        d="M0,240 L0,130 Q 90,100 180,130 T 360,120 T 540,135 T 720,115 T 900,130 T 1080,120 T 1200,130 L1200,240 Z"
        fill="#070b14"
      />
    </svg>
  </div>
{/if}

{#if $pasoActual === 'CARTA'}
  <Carta abierta={true} frases={FRASES_PLACEHOLDER} urlFoto="/images/placeholder.jpg" />
{:else if $pasoActual !== 'GIRASOLES' && $pasoActual !== 'AMANECER'}
  {@const boton = botonSiguiente($pasoActual)}
  {#if boton}
    <div
      class="contenedor-botones"
      class:boton-elevado={$pasoActual === 'SOL' && ($tiempoReproduccion >= 11 || forzarAceleracion)}
    >
      <Boton etiqueta={boton.etiqueta} alPulsar={manejarAvance} variante="recorrido" />
    </div>
  {/if}
{/if}

<style>
  .contenedor-botones {
    position: fixed;
    bottom: var(--espaciado-xl);
    left: 50%;
    transform: translateX(-50%);
    z-index: 100;
    display: flex;
    gap: var(--espaciado-m);
    transition:
      bottom 2.2s cubic-bezier(0.25, 1, 0.5, 1),
      transform 2.2s cubic-bezier(0.25, 1, 0.5, 1);
  }

  .contenedor-botones.boton-elevado {
    bottom: 240px; /* Ascenso pausado y sutil */
  }

  .zona-poema-con-boton {
    position: fixed;
    top: 14%;
    left: 50%;
    transform: translateX(-50%);
    width: 90%;
    max-width: 580px;
    z-index: 25;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 18px;
    pointer-events: none;
    animation: aparecerPoema 1.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }

  .zona-poema-con-boton .cartel-poema {
    position: static;
    top: auto;
    left: auto;
    transform: none;
    width: 100%;
    animation: none;
  }

  .contenedor-boton-bajo-caja {
    pointer-events: auto;
    display: flex;
    justify-content: center;
    animation: aparecerBotonBajoCaja 1.2s cubic-bezier(0.16, 1, 0.3, 1) 0.6s backwards;
  }

  @keyframes aparecerBotonBajoCaja {
    0% {
      opacity: 0;
      transform: translateY(12px) scale(0.94);
    }
    100% {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  /* Escenario final en Amanecer: elementos distribuidos verticalmente más arriba */
  .escenario-final-amanecer {
    position: fixed;
    top: 5%;
    left: 50%;
    transform: translateX(-50%);
    width: 90%;
    max-width: 520px;
    z-index: 25;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    pointer-events: none;
    animation: aparecerEscenarioFinal 2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }

  @keyframes aparecerEscenarioFinal {
    0% {
      opacity: 0;
      transform: translateX(-50%) translateY(20px);
    }
    100% {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }

  /* 1. Cuadro Polaroid independiente encima de la caja */
  .polaroid-final {
    margin: 0 auto;
    padding: 8px 8px 24px;
    background: #ffffff;
    box-shadow:
      0 10px 25px rgba(0, 0, 0, 0.2),
      0 2px 6px rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    transform: rotate(-1.5deg);
    transition: transform 0.4s ease;
  }

  .polaroid-final:hover {
    transform: rotate(0deg) scale(1.03);
  }

  .img-polaroid-final {
    width: 140px;
    height: 140px;
    aspect-ratio: 1 / 1;
    object-fit: cover;
    display: block;
    border-radius: 2px;
  }

  /* 2. Caja de texto translúcida (ajusta la opacidad aquí abajo) */
  .caja-texto-amanecer {
    width: 100%;
    padding: 18px 24px;
    /* 🎚️ TRANSPARENCIA DE LA CAJA:
       Cambia el '0.35' y '0.45' (0.0 = totalmente transparente, 1.0 = totalmente sólida) */
    background: radial-gradient(
      ellipse at 50% 50%,
      rgba(255, 252, 240, 0.38) 0%,
      rgba(247, 235, 210, 0.48) 100%
    );
    border: 1px solid rgba(255, 255, 255, 0.45);
    border-radius: 20px;
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    box-shadow:
      0 8px 30px rgba(0, 0, 0, 0.15),
      0 0 20px rgba(255, 230, 160, 0.2);
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }

  .adorno-amanecer {
    font-size: 1.15rem;
    color: #df6d22;
  }

  .frase-dedicatoria-amanecer {
    font-family: var(--familia-tipografica);
    font-size: clamp(1rem, 3.8vw, 1.22rem);
    font-style: italic;
    font-weight: 600;
    color: #4a341e;
    line-height: 1.5;
    margin: 0;
  }

  .frase-poema-amanecer {
    font-family: var(--familia-tipografica);
    font-size: clamp(0.9rem, 3.2vw, 1.05rem);
    color: #5d452d;
    line-height: 1.6;
    margin: 0;
  }

  /* Frase de cierre final: Sigamos cultivando nuestro amor */
  .frase-cierre-final {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
    padding: 8px 22px;
    backdrop-filter: blur(5px);
    -webkit-backdrop-filter: blur(5px);
    background: linear-gradient(135deg, rgba(245, 176, 49, 0.24) 0%, rgba(223, 110, 34, 0.24) 100%);
    border: 1px solid rgba(223, 109, 34, 0.35);
    border-radius: 9999px;
    box-shadow: 0 2px 10px rgba(245, 176, 49, 0.2);
    text-align: center;
  }

  .texto-cierre {
    font-family: var(--familia-tipografica);
    font-size: clamp(0.92rem, 3.1vw, 1.08rem);
    font-weight: 600;
    font-style: italic;
    color: #9c450c;
    letter-spacing: 0.02em;
  }

  .adorno-cierre {
    font-size: 1.1rem;
  }

  /* Girasol emergiendo suavemente en la base */
  .girasol-base-revelado {
    position: fixed;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    z-index: 15;
    pointer-events: none;
    display: flex;
    justify-content: center;
    animation: brotarGirasolBase 2.6s cubic-bezier(0.25, 1, 0.5, 1) forwards;
  }

  .girasol-base-img {
    width: 210px;
    height: auto;
    filter: drop-shadow(0 0 25px rgba(242, 177, 53, 0.45));
    animation: mecerGirasol 5s ease-in-out infinite alternate;
  }

  @keyframes brotarGirasolBase {
    0% {
      opacity: 0;
      transform: translateX(-50%) translateY(70px) scale(0.85);
    }
    100% {
      opacity: 1;
      transform: translateX(-50%) translateY(10px) scale(1);
    }
  }

  @keyframes mecerGirasol {
    0% {
      transform: rotate(-2deg);
    }
    100% {
      transform: rotate(2deg);
    }
  }



  /* Pensamiento que aparece a la derecha del girasol si se acelera */
  .pensamiento-apurada {
    position: absolute;
    bottom: 110px;
    left: 82%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    pointer-events: none;
    animation: aparecerPensamiento 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
    z-index: 25;
    width: max-content;
    max-width: 280px;
  }

  .punto-pensamiento {
    display: block;
    border-radius: 50%;
    background: rgba(255, 248, 230, 0.95);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }

  .punto-pensamiento.p1 {
    width: 8px;
    height: 8px;
    margin-left: -14px;
    margin-bottom: 4px;
  }

  .punto-pensamiento.p2 {
    width: 13px;
    height: 13px;
    margin-left: -6px;
    margin-bottom: 5px;
  }

  .burbuja-pensamiento {
    background: rgba(255, 248, 230, 0.96);
    color: #2b2418;
    font-family: var(--familia-tipografica);
    font-size: 0.95rem;
    font-style: italic;
    line-height: 1.45;
    padding: 10px 16px;
    border-radius: 16px;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.35);
    border: 1px solid rgba(242, 177, 53, 0.5);
  }

  @keyframes aparecerPensamiento {
    0% {
      opacity: 0;
      transform: scale(0.7) translateY(10px);
    }
    100% {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  .prado-recorrido {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 140px;
    pointer-events: none;
    z-index: 3;
  }

  .capa-hierba-recorrido {
    width: 100%;
    height: 100%;
  }

  /* Cartel poético que aparece suavemente con el canto */
  .cartel-poema {
    position: fixed;
    top: 22%;
    left: 50%;
    transform: translateX(-50%);
    width: 90%;
    max-width: 580px;
    padding: 24px 28px;
    background: radial-gradient(
      ellipse at 50% 50%,
      rgba(26, 34, 56, 0.75) 0%,
      rgba(15, 20, 35, 0.85) 100%
    );
    border: 1px solid rgba(255, 235, 150, 0.3);
    border-radius: 20px;
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    box-shadow:
      0 12px 35px rgba(0, 0, 0, 0.45),
      0 0 30px rgba(255, 215, 112, 0.12);
    z-index: 20;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    pointer-events: none;
    animation: aparecerPoema 2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }

  .adorno-poema {
    color: var(--color-luciernaga);
    font-size: 1.1rem;
    opacity: 0.85;
    animation: pulsoAdorno 3s ease-in-out infinite alternate;
  }

  @keyframes pulsoAdorno {
    0% { transform: scale(0.9); opacity: 0.5; }
    100% { transform: scale(1.2); opacity: 1; }
  }

  .frase-dedicatoria {
    font-family: var(--familia-tipografica);
    font-size: clamp(1rem, 3.8vw, 1.25rem);
    font-style: italic;
    color: #fff4d1;
    line-height: 1.6;
    margin: 0;
    letter-spacing: 0.02em;
    text-shadow: 0 2px 10px rgba(0, 0, 0, 0.6);
  }

  .frase-poema {
    font-family: var(--familia-tipografica);
    font-size: clamp(0.95rem, 3.5vw, 1.15rem);
    color: rgba(244, 234, 213, 0.85);
    line-height: 1.7;
    margin: 0;
    letter-spacing: 0.02em;
    border-top: 1px solid rgba(255, 235, 150, 0.15);
    padding-top: 12px;
  }

  @keyframes aparecerPoema {
    0% {
      opacity: 0;
      transform: translate(-50%, 18px) scale(0.97);
    }
    100% {
      opacity: 1;
      transform: translate(-50%, 0) scale(1);
    }
  }

  @media (max-width: 480px) {
    .cartel-poema {
      top: 13%;
      padding: 16px 18px;
      gap: 10px;
    }
    .contenedor-botones.boton-elevado {
      bottom: 180px;
    }
    .girasol-base-img {
      width: 165px;
    }
    .pensamiento-apurada {
      left: 50%;
      bottom: 90px;
      transform: translateX(-5%);
      max-width: 220px;
    }
    .burbuja-pensamiento {
      font-size: 0.82rem;
      padding: 8px 12px;
      white-space: normal;
      width: 100%;
      text-align: left;
    }
  }
</style>
